import { BRAND_INTERNATIONAL, OFFICIAL_SLOGAN, STORE_NAME_UA } from "../brand.js";

export const messagesEn = {
  brand: {
    name: STORE_NAME_UA,
    international: BRAND_INTERNATIONAL,
    tagline: OFFICIAL_SLOGAN,
    slogan: OFFICIAL_SLOGAN,
  },
  nav: {
    home: "Home",
    catalog: "Catalog",
    cart: "Cart",
    profile: "Profile",
  },
  common: {
    retry: "Try again",
    loadMore: "Load more",
    back: "Back",
    close: "Close",
    searchPlaceholder: "Search products…",
    noImage: "No image",
    customerFallback: "Guest",
    currency: "₴",
    soon: "Soon",
  },
  session: {
    connecting: "Connecting session…",
    unavailable: "Could not open session",
    errorTitle: "Something went wrong",
    telegramOnly: "Open the app inside Telegram to authenticate.",
  },
  home: {
    greeting: (name: string) => `Welcome, ${name}`,
    heroTitle: OFFICIAL_SLOGAN,
    heroSubtitle: "Discover Asian flavors and order with Nova Poshta delivery in a few taps.",
    heroCta: "Browse catalog",
    categoriesTitle: "Categories",
    popularTitle: "Popular products",
    newTitle: "New arrivals",
    promoTitle: "Promotions",
    recommendedTitle: "Recommended for you",
    cartShortcut: "Go to cart",
    browseCatalog: "Open catalog",
    emptyCatalogHint: "Catalog is updating. Try again later or contact support.",
    categoriesLoading: "Loading categories…",
    categoriesEmpty: "Categories will appear after product sync.",
  },
  catalog: {
    title: "Catalog",
    sortLabel: "Sort",
    filterLabel: "Filter",
    sortDefault: "Default",
    sortPriceAsc: "Price: low to high",
    sortPriceDesc: "Price: high to low",
    sortName: "Name",
    allCategories: "All categories",
    loading: "Loading catalog…",
    openingProduct: "Opening product…",
    addToCart: "Add to cart",
    adding: "Adding…",
    added: "Added to cart",
    inStock: "In stock",
    outOfStock: "Out of stock",
    emptyTitle: "No products in this filter",
    emptySearchTitle: "Nothing found",
    emptyBody: "Try another category or reset filters — the catalog is not empty.",
    emptySearchBody: "Try another query or reset filters.",
    emptyCategoryTitle: "No products in this category right now",
    emptyCategoryBody: "Pick another category or open all categories.",
    resetFilters: "Reset filters",
    showMoreCategories: "More",
    loadMoreFailed: "Could not load more",
    catalogFailed: "Could not load catalog",
  },
  product: {
    backToCatalog: "← Back to catalog",
    brand: "Brand",
    category: "Category",
    country: "Country",
    weightOrVolume: "Weight / volume",
    stock: "Availability",
    quantity: "Quantity",
    relatedTitle: "Frequently bought together",
    stickyAdd: "Add to cart",
  },
  cart: {
    title: "Cart",
    itemsCount: (count: number) => `${count} items`,
    loading: "Loading cart…",
    emptyTitle: "Cart is empty",
    emptyBody: "Add products from the catalog — checkout takes just a few minutes.",
    emptyCta: "Go to catalog",
    clear: "Clear cart",
    checkout: "Checkout",
    checkoutUnavailable: "Checkout temporarily unavailable",
    remove: "Remove",
    unitPrice: (price: string) => `${price} / ea.`,
    subtotal: "Subtotal",
    discount: "Discount",
    total: "Total",
    priceUpdatedTitle: "Prices updated",
    priceUpdatedBody: "Current prices were applied to your cart before checkout.",
    stagingNote: "",
    promoTitle: "Promo code",
    promoHint: "TG50: −50 UAH from 500 UAH, once. Not combinable.",
    promoPlaceholder: "TG50",
    promoApply: "Apply",
    promoRemove: "Remove",
    promoUnknown: "Unknown promo code",
    promoTg50Applied: (uah: number) => `TG50 applied: −${uah} UAH`,
    promoTg50UnderMin: (min: number) => `TG50 needs ${min}+ UAH — discount not active yet`,
    promoAppliedOk: "Promo accepted",
    promoApplyFailed: "Could not apply promo",
  },
  checkout: {
    title: "Checkout",
    backToCart: "← Back to cart",
    summary: "Your order",
    contact: "Contact details",
    delivery: "Delivery",
    payment: "Payment",
    fullName: "Full name *",
    phone: "Phone *",
    method: "Method *",
    city: "City *",
    warehouse: "Branch / parcel locker *",
    address: "Address *",
    comment: "Comment",
    loading: "Preparing checkout…",
    emptyCart: "Cart is empty. Add products before checkout.",
    browseCatalog: "Go to catalog",
    unavailable: "Cart unavailable",
    submit: "Confirm order",
    submitting: "Submitting…",
    confirmPriceChanges: "Confirm updated prices",
    priceChangeTitle: "Prices changed",
    priceChangeBody: "Confirm current prices before submitting your order.",
    paymentNote:
      "Online payment is off. After order status is payment pending — manager confirms manually. No acquiring link is created.",
    npNote:
      "Nova Poshta: choose a city and branch or parcel locker for delivery.",
    tg50Note:
      "TG50: −50 UAH from 500 UAH, once per customer, not combinable. Confirmed by system/manager.",
    stagingBetaBanner: "",
    deliveryOptions: {
      nova_poshta_warehouse: "Nova Poshta — branch",
      nova_poshta_courier: "Nova Poshta — courier",
      pickup: "Pickup",
      other: "Other",
    },
    paymentOptions: {
      cod: "Cash on delivery (manager confirmation)",
      prepaid: "Prepayment (manual confirmation)",
      novapay: "Online card payment",
      manager: "Agree payment with manager",
    },
    successTitle: "Order accepted",
    successBody:
      "Status: payment pending (manual). Online acquiring and live Nova Poshta TTN are off. A manager will follow up.",
    successOrderRef: (id: string) => `Order number: ${id}`,
    successTaskRef: (id: string) => `Manager task: ${id}`,
    orderNumberLabel: "Order number",
    managerTaskLabel: "Manager task",
    backToCatalog: "Back to catalog",
  },
  profile: {
    title: "Profile",
    phone: "Phone",
    phoneMissing: "Not provided",
    segment: "Segment",
    orders: "My orders",
    ordersPlaceholder: "Order history will appear here soon.",
    favorites: "Favorites",
    favoritesSoon: "Soon",
    recentlyViewed: "Recently viewed",
    recentlyViewedSoon: "Soon",
    repeatPurchase: "Repeat order",
    repeatPurchaseSoon: "Soon",
    deliveryAddresses: "Delivery addresses",
    deliveryAddressesSoon: "Soon",
    loyalty: "Bonuses & loyalty",
    loyaltySoon: "Soon",
    support: "Contact support",
    supportHint: "A manager can help with your order and delivery.",
    buildIdLabel: "Build ID",
  },
  segment: {
    new: "New",
    active: "Active",
    vip: "VIP",
    at_risk: "Needs attention",
    churned: "Inactive",
  },
} as const;
